import { CheckCircle2, XCircle, Package, Truck, Store, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { STORE_ADDRESS, STORE_PHONE, DELIVERY, PALLET } from "@/constants/store";

// Reusable policy blocks. Each block is a self-contained visual card that
// renders on department category + product pages. The website spec requires
// each department's policy be visible ON the page, not just linked from the footer.

function PolicyCard({ icon: Icon, title, children, tone = "navy", testid }) {
  const bg = tone === "gold" ? "bg-[#FFC220]/10 border-[#FFC220]" : "bg-gray-50 border-gray-300";
  const accent = tone === "gold" ? "text-[#8a6a00]" : "text-[#0A1C40]";
  return (
    <div className={`border-l-4 ${bg} p-6`} data-testid={testid}>
      <div className={`flex items-center gap-2 ${accent}`}>
        <Icon className="w-4 h-4" />
        <div className="ipv-eyebrow font-black">{title}</div>
      </div>
      <div className="mt-3 text-sm text-gray-700 leading-relaxed space-y-2">
        {children}
      </div>
    </div>
  );
}

function DeliveryPickupBlock() {
  return (
    <PolicyCard icon={Truck} title="Delivery & Pickup" testid="policy-delivery-pickup">
      <p>
        <b>Pickup is always free</b> — ready during store hours at {STORE_ADDRESS.short}.
        Mon–Sat 10:00 AM – 7:00 PM · Sun 12:00 PM – 5:00 PM.
      </p>
      <p>
        <b>Delivery fees</b> are quoted at checkout based on your address.
        FREE on orders $299+ within 10 miles. $39 under $299 within 10 miles.
        $49 for 10–25 miles. Add $2/mile beyond 25 miles.
        Deliveries run <b>Tuesday · Thursday · Saturday</b>.
        Orders paid by 2 PM go on the next delivery day.
      </p>
      <Link to="/policies/delivery" className="text-[#0A1C40] font-bold underline text-xs">
        See full delivery policy →
      </Link>
    </PolicyCard>
  );
}

function ReturnBlock({ extra48hr }) {
  return (
    <PolicyCard icon={ShieldCheck} title="Returns & Exchanges" testid="policy-returns">
      <p>
        We are a closeout &amp; overstock retailer. <b>All items sell as-is</b>.
        Please inspect your item before you pay.
      </p>
      <p>
        <b>14-day exchange or store credit</b> — item must be unused, in original
        packaging, with receipt and photo ID. <b>We do not issue cash refunds.</b>
      </p>
      {extra48hr && (
        <p className="text-[#0A1C40] font-semibold">
          Appliances &amp; electronics: test your item before you leave the store.
          If it does not work, tell us within 48 hours for exchange or store credit.
        </p>
      )}
      <Link to="/policies/returns" className="text-[#0A1C40] font-bold underline text-xs">
        See full return policy →
      </Link>
    </PolicyCard>
  );
}

function EbikeFinalSaleBlock() {
  return (
    <PolicyCard icon={XCircle} title="E-Bikes & Scooters — Final Sale Once Ridden" tone="gold" testid="policy-ebike-finalsale">
      <p>
        E-bikes and scooters are <b>final sale once ridden</b>. Please inspect,
        test-ride in-store if needed, and confirm the item is right for you BEFORE
        you leave. If defective, bring it back with your receipt.
      </p>
      <Link to="/policies/returns" className="text-[#0A1C40] font-bold underline text-xs">
        See full return policy →
      </Link>
    </PolicyCard>
  );
}

function PalletBlock() {
  return (
    <PolicyCard icon={Package} title="Pallet Guarantee & Terms" tone="gold" testid="policy-pallet">
      <p className="font-bold text-[#0A1C40]">Our Pallet Guarantee — store credit only:</p>
      <ul className="list-disc pl-5 space-y-1">
        {PALLET.guarantee.map((line) => <li key={line}>{line}</li>)}
      </ul>
      <p className="font-bold text-[#0A1C40] mt-3">Delivery / pickup:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Delivery: <b>${PALLET.deliveryFlat10mi} within 10 miles · ${PALLET.deliveryFlat25mi} within 25 miles</b> · quote beyond. Curbside/driveway only.</li>
        <li>Pickup: by appointment. Bring a truck, trailer, or van. We load, you strap &amp; secure.</li>
        <li>Pick up within 48 hours of payment; $25/day storage after that. Forfeited after 10 days.</li>
      </ul>
      <p><b>Pallets sell whole.</b> No cherry-picking. 6% sales tax applies. Resale buyers must have Form 3372 on file before purchase.</p>
      <Link to="/policies/pallets" className="text-[#0A1C40] font-bold underline text-xs">
        See full pallet policy →
      </Link>
    </PolicyCard>
  );
}

function ResellerBlock() {
  return (
    <PolicyCard icon={Store} title="Reseller / Wholesale" testid="policy-reseller">
      <p>
        Registered resellers can buy sales-tax-exempt. Bring your business's
        Michigan Form 3372 to the store before your first purchase, or email
        it to us and we'll add you to our tax-exempt list.
      </p>
      <p>Call <a href={`tel:+1${STORE_PHONE.replace(/\D/g, "")}`} className="underline font-bold">{STORE_PHONE}</a> to set up a reseller account.</p>
    </PolicyCard>
  );
}

// Map department → policy blocks to render
const DEPT_MAP = {
  "Furniture": [DeliveryPickupBlock, ReturnBlock],
  "Mattresses": [DeliveryPickupBlock, ReturnBlock],
  "Bedroom": [DeliveryPickupBlock, ReturnBlock],
  "Living Room": [DeliveryPickupBlock, ReturnBlock],
  "Dining": [DeliveryPickupBlock, ReturnBlock],
  "Recliners": [DeliveryPickupBlock, ReturnBlock],
  "Appliances": [DeliveryPickupBlock, () => <ReturnBlock extra48hr />],
  "Electronics": [DeliveryPickupBlock, () => <ReturnBlock extra48hr />],
  "E-Bikes": [DeliveryPickupBlock, EbikeFinalSaleBlock],
  "Scooters": [DeliveryPickupBlock, EbikeFinalSaleBlock],
  "General Merchandise": [DeliveryPickupBlock, ReturnBlock],
  "Deals Under $20": [DeliveryPickupBlock, ReturnBlock],
  "Landscaping": [DeliveryPickupBlock, () => <ReturnBlock extra48hr />],
  "Closeout & Overstock Pallets": [PalletBlock],
  "Pallets": [PalletBlock],
};

/**
 * Renders the policy block(s) for a given department. Falls back to Delivery
 * + Returns for unknown categories.
 * Usage:
 *   <DepartmentPolicy department={product.category} />
 *   <DepartmentPolicy department="Closeout & Overstock Pallets" />
 */
export function DepartmentPolicy({ department }) {
  const blocks = DEPT_MAP[department] || [DeliveryPickupBlock, ReturnBlock];
  return (
    <section className="mt-16 border-t border-gray-200 pt-10" data-testid="department-policy" data-department={department}>
      <div className="ipv-eyebrow text-gray-500 mb-4">Store Policies — {department}</div>
      <div className="grid gap-4 md:grid-cols-2">
        {blocks.map((Block, i) => <Block key={i} />)}
      </div>
    </section>
  );
}
